import { useEffect, useSyncExternalStore } from "react";
import { useAiChatActions } from "../../aiChat/AiChatContext";
import { paneStore } from "./paneStore";
import { PaneDefinition, toOpenPane } from "./definePane";
import { useOwningPaneInstanceId } from "./paneLayout/usePane";
import { FrameId, frameIds, getPaneInSlot, hasPaneOnRight, hasPaneOnStage, Placement } from "./paneRules";

function findFrameShowing<TProps>(placement: Placement, pane: PaneDefinition<TProps>, matches: (props: TProps) => boolean): FrameId | undefined {
    return frameIds.find((frame) => {
        const open = placement[frame].pane;
        return open !== undefined && open.definition === pane && matches(open.props as TProps);
    });
}
// Built once: every member is a plain function of the store, so a caller can put any of them in an
// effect's dependencies without the effect re-running.
const paneActions = {
    open: <TProps>(pane: PaneDefinition<TProps>, props: TProps) => paneStore.open(toOpenPane(pane, props)),
    /** Opens a stage pane beside the one the stage is showing: that pane moves into the rail while the rail is free. */
    openBeside: <TProps>(pane: PaneDefinition<TProps>, props: TProps) => paneStore.openBeside(toOpenPane(pane, props)),
    closeSlot: paneStore.closeSlot,
    requestCloseSlot: paneStore.requestCloseSlot,
    /**
     * Closes a particular pane wherever it is, asking first when it has something to lose. For a page that owns a
     * pane and knows it by what it opened, rather than by the slot it landed in.
     */
    requestClosePane: <TProps>(pane: PaneDefinition<TProps>, matches: (props: TProps) => boolean) => {
        const frame = findFrameShowing(paneStore.getState().placement, pane, matches);
        if (frame !== undefined) {
            paneStore.requestCloseFrame(frame);
        }
    },
    /**
     * Closes a particular pane, asking first when it has something to lose, and then does what the user set out to
     * do. A cancelled discard does nothing, so the user stays with the pane. A pane that is not open lets it go ahead.
     */
    requestClosePaneThen: <TProps>(pane: PaneDefinition<TProps>, matches: (props: TProps) => boolean, afterwards: () => void) => {
        const frame = findFrameShowing(paneStore.getState().placement, pane, matches);
        if (frame === undefined) {
            afterwards();
        } else {
            paneStore.requestCloseFrameThen(frame, afterwards);
        }
    },
    /** Closes a particular pane without asking. For a page that is already gone, where asking can no longer keep the user. */
    closePane: <TProps>(pane: PaneDefinition<TProps>, matches: (props: TProps) => boolean) => {
        const frame = findFrameShowing(paneStore.getState().placement, pane, matches);
        if (frame !== undefined) {
            paneStore.closeFrame(frame);
        }
    },
    refetchPageBehindPanes: paneStore.refetchPageBehindPanes,
};

/** What a page uses to show panes. Subscribes to nothing, so opening a pane never re-renders the page that opened it. */
export function usePanes() {
    return paneActions;
}

function usePaneState<T>(select: (state: ReturnType<typeof paneStore.getState>) => T): T {
    return useSyncExternalStore(paneStore.subscribe, () => select(paneStore.getState()));
}

/** What is showing where, for the few components that lay out around the panes. */
export function usePaneSlots() {
    return {
        hasPaneOnRight: usePaneState((state) => hasPaneOnRight(state.placement)),
        hasPaneOnStage: usePaneState((state) => hasPaneOnStage(state.placement)),
        rightPane: usePaneState((state) => getPaneInSlot(state.placement, "right")),
    };
}

/**
 * Whether a pane of this kind is open anywhere, the rail or the stage, and matching the given props. For
 * something that follows the panes, such as a url parameter, to read rather than be called back by them.
 */
export function useIsPaneOpen<TProps>(pane: PaneDefinition<TProps>, matches: (props: TProps) => boolean): boolean {
    return usePaneState((state) => findFrameShowing(state.placement, pane, matches) !== undefined);
}

export function usePlacement() {
    return {
        placement: usePaneState((state) => state.placement),
        confirming: usePaneState((state) => state.confirming),
    };
}

/**
 * Lets the page behind the panes refresh itself when one of them saves. Every list that opens an
 * obligation calls this with its own refetch, in place of the onSave prop it used to pass the dialog.
 */
export function useRefetchWhenPaneSaves(refetch: () => void) {
    const ownerInstanceId = useOwningPaneInstanceId();

    // Re-registers on every render so the closure stays current without callers having to stabilise theirs.
    // Cheap, because registering only touches a Set.
    useEffect(() => paneStore.registerRefetch(refetch, ownerInstanceId));
}

/**
 * Toggling the chat goes through here rather than straight to the chat context: the rail holds one thing at
 * a time, so opening the chat has to clear whatever the rail was showing, sending a stage pane home when the
 * stage is free. Vacating unconditionally is safe: when the chat is open the rail is already empty, and
 * vacating is a no-op then. It still asks first, because retiring the rail this way loses exactly what
 * pressing its close button would.
 */
export function useToggleAiChat() {
    const { toggleOpen } = useAiChatActions();
    return () => paneStore.vacateRightThen(toggleOpen);
}
